import {
  ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { Select, MenuItem, Typography } from '@mui/material'
import {
  getCountry,
  defaultCountries,
  parseCountry,
} from 'react-international-phone'
import { ParsedCountry } from 'react-international-phone/build/types'
import { LIMITS } from '@weblite/permission'
import { toEnglishNumber, toLocale } from '$func/text'
// Localization
import { t } from '$i18n'
import { ArrowDropDownIcon } from '$common/icons'
// eslint-disable-next-line import/no-unresolved
import 'react-international-phone/style.css'
import { EMOJI_SPRITE_SHEET } from '$helper/constants/emoji'
import { addCountryCodeToNumber } from '$helper/functions/phone'
import { TextInput } from './text-input'

const { userConstraints } = LIMITS

interface Props {
  value: string
  onChange: (value: string) => void
  hasError?: boolean
  errorMessage?: string
}

export interface PhoneInputRef {
  getPhone: (phone: string) => string
}

export const PhoneInput = forwardRef(function PhoneInput(
  {
    value: rawPhoneNumber,
    onChange: setPhoneNumber,
    hasError = false,
    errorMessage = '',
  }: Props,
  ref: ForwardedRef<PhoneInputRef>,
) {
  const filteredCountries = useMemo(() => {
    return defaultCountries.filter(
      country => parseCountry(country).iso2 !== 'il',
    )
  }, [])

  const [selectedCountry, setSelectedCountry] =
    useState<ParsedCountry['iso2']>('ir')
  const countryCode = useMemo(() => {
    return getCountry({ field: 'iso2', value: selectedCountry })!.dialCode
  }, [selectedCountry])

  useImperativeHandle(ref, () => ({
    getPhone: (phone: string) => {
      return addCountryCodeToNumber(phone, countryCode || '98')
    },
  }))

  const [anchorEl, setAnchorEl] = useState<EventTarget & Element>()

  return (
    <div style={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
      <TextInput
        startAdornment={
          <div style={{ marginRight: '20px' }}>
            {toLocale(countryCode || '')}
          </div>
        }
        autoComplete="tel"
        placeholder={t('contacts.phonePlaceholder')}
        value={toLocale(rawPhoneNumber)}
        onChange={newVal => setPhoneNumber(toEnglishNumber(newVal))}
        direction="ltr"
        successMessage=""
        errorMessage={errorMessage}
        hasError={hasError}
        disableLengthError
        maxLength={
          selectedCountry === 'ir' ? userConstraints.phone.maxLength : undefined
        }
        inputProps={{ type: 'tel' }}
      />

      <Select
        MenuProps={{
          anchorEl,
          slotProps: {
            paper: {
              sx: {
                borderRadius: '12px',
                overflow: 'overlay',
              },
            },
          },
          anchorOrigin: { vertical: 'top', horizontal: 'left' },
          style: {
            borderRadius: '12px',
            height: '300px',
            top: '-10px',
          },
          transformOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        }}
        sx={{
          height: '48px',
          marginTop: '8px',
          borderRadius: '12px',
          border: `1px solid`,
          borderColor: 'neutral300',
          width: 'max-content',
          // Remove default outline (display only on focus)
          fieldset: {
            display: 'none',
          },

          '&.Mui-focused': {
            borderColor: 'primary300',
          },

          '&.Mui-focused:has(div[aria-expanded="false"])': {
            borderColor: 'neutral300',
          },
          // Update default spacing
          '.MuiSelect-select': {
            padding: '8px',
            paddingRight: '24px !important',
          },
          svg: {
            right: 0,
          },
        }}
        IconComponent={ArrowDropDownIcon}
        value={selectedCountry}
        onOpen={e => setAnchorEl(e.currentTarget)}
        onChange={e => setSelectedCountry(e.target.value)}
        renderValue={value => (
          <em-emoji
            id={`flag-${value}`}
            set="apple"
            spritesheeturl={EMOJI_SPRITE_SHEET}
            spritesheet
            size={18} // TODO: use app font or rem or scale for zoom option
          />
        )}
      >
        {filteredCountries.map(c => {
          const country = parseCountry(c)
          return (
            <MenuItem
              key={country.iso2}
              value={country.iso2}
              style={{
                flexDirection: 'row-reverse',
                borderRadius: '8px',
                padding: '6px 8px',
              }}
              sx={{
                margin: '4px 8px 0',
                '&.Mui-selected': {
                  backgroundColor: 'secondary50',
                },
              }}
            >
              <em-emoji
                id={`flag-${country.iso2}`}
                style={{ marginRight: '8px' }}
                set="apple"
                spritesheeturl={EMOJI_SPRITE_SHEET}
              />
              <Typography variant="body1" color="neutral900" marginRight="8px">
                {country.name}
              </Typography>
              <Typography
                variant="subtitle1"
                color="neutral600"
                sx={{ marginLeft: 'auto' }}
              >
                {toLocale(country.dialCode)}+
              </Typography>
            </MenuItem>
          )
        })}
      </Select>
    </div>
  )
})
